const VISIBLE_CLASS = 'sticky-cta-wrapper--visible';
const BUTTON_CLASS = 'sticky-cta-button';
const CLOSES_IN_SUFFIX = ' closes in';
const DRAW_TITLE_PREFIX = 'Draw ';
const DRAW_NUMBER_PARAM = 'drawNumber';
const CART_URL_ATTRIBUTE = 'data-cart-url';
const ORIGIN_PREFIX = /^[a-z]+:\/\/[^/]+/i;
const TRAILING_SLASH = /\/$/;

const WRAPPER_SELECTOR = '.sticky-cta-wrapper';
const TITLE_SELECTOR = '.sticky-cta-title';
const COUNTDOWN_SELECTOR = '.sticky-cta-countdown';
const DRAW_TITLE_SELECTOR = '.yt-o-prize-home-page-heading__super-heading';
const COUNTDOWN_SOURCE_SELECTOR = '.yt-o-countdown';
const PRIZE_INFO_SELECTOR = '.yt-o-prize-info';
const PRIZE_INFO_CTA_SELECTOR = '.yt-o-prize-info a.yt-btn-primary:not(.disabled)';
const HEADER_CTA_SELECTOR = '.yt-o-header a.yt-btn-primary:not(.disabled)';

const SOURCE_SELECTORS = [PRIZE_INFO_CTA_SELECTOR, HEADER_CTA_SELECTOR];

const pathOf = (url: string) => url.replace(ORIGIN_PREFIX, '').split('#')[0].split('?')[0].replace(TRAILING_SLASH, '');

const isVisible = (element: Element) =>
  typeof element.checkVisibility === 'function'
    ? element.checkVisibility({ contentVisibilityAuto: true, opacityProperty: true, visibilityProperty: true })
    : element.getClientRects().length > 0;

const leadsToCart = (anchor: HTMLAnchorElement, cartPath: string) => {
  if (!cartPath) return true;

  const path = pathOf(anchor.getAttribute('href') ?? '');

  return path === cartPath || path.startsWith(cartPath + '/');
};

const findSources = (root: ParentNode, cartPath: string) => {
  for (const selector of SOURCE_SELECTORS) {
    const matches = Array.from(root.querySelectorAll<HTMLAnchorElement>(selector)).filter((anchor) => leadsToCart(anchor, cartPath));

    if (matches.some(isVisible)) return matches;
  }

  return [];
};

const resolveTitle = (root: ParentNode, source: HTMLAnchorElement) => {
  const heading = source.closest(PRIZE_INFO_SELECTOR) ? root.querySelector<HTMLElement>(DRAW_TITLE_SELECTOR)?.textContent?.trim() : '';
  const [addressable] = (source.getAttribute('href') ?? '').split('#');
  const [, query] = addressable.split('?');
  const drawNumber = query ? new URLSearchParams(query).get(DRAW_NUMBER_PARAM) : null;
  const drawTitle = heading || (drawNumber ? DRAW_TITLE_PREFIX + drawNumber : '');

  if (!drawTitle) return '';

  return root.querySelector(COUNTDOWN_SOURCE_SELECTOR) ? drawTitle + CLOSES_IN_SUFFIX : drawTitle;
};

const syncCountdown = (target: HTMLElement, source: HTMLElement) => {
  const sync = () => {
    target.textContent = source.textContent?.trim() ?? '';
  };

  sync();
  new MutationObserver(sync).observe(source, { characterData: true, childList: true, subtree: true });
};

const cloneButton = (wrapper: HTMLElement, source: HTMLAnchorElement) => {
  const button = source.cloneNode(true) as HTMLAnchorElement;

  button.classList.add(BUTTON_CLASS);
  button.removeAttribute('id');

  wrapper.querySelector(`.${BUTTON_CLASS}`)?.remove();
  wrapper.appendChild(button);
};

const observeVisibility = (wrapper: HTMLElement, sources: HTMLAnchorElement[]) => {
  const inViewport = new Set<Element>();

  let observer: IntersectionObserver | null = null;
  let barHeight = -1;

  const connect = () => {
    if (wrapper.offsetHeight === barHeight) return;

    barHeight = wrapper.offsetHeight;
    observer?.disconnect();
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isVisible(entry.target)) {
            inViewport.add(entry.target);

            return;
          }

          inViewport.delete(entry.target);
        });

        wrapper.classList.toggle(VISIBLE_CLASS, inViewport.size === 0);
      },
      { rootMargin: '0px' }
    );

    sources.forEach((source) => observer?.observe(source));
  };

  const resize = 'ResizeObserver' in window ? new ResizeObserver(connect) : null;

  connect();
  resize?.observe(wrapper);
};

export const setupStickyCta = (root: ParentNode = document) => {
  const wrapper = root.querySelector<HTMLElement>(WRAPPER_SELECTOR);
  const sources = findSources(root, pathOf(wrapper?.getAttribute(CART_URL_ATTRIBUTE) ?? ''));
  const source = sources.find(isVisible) ?? sources[0];

  if (!wrapper || !source || !('IntersectionObserver' in window)) return;

  const title = wrapper.querySelector<HTMLElement>(TITLE_SELECTOR);
  const countdown = wrapper.querySelector<HTMLElement>(COUNTDOWN_SELECTOR);
  const countdownSource = root.querySelector<HTMLElement>(COUNTDOWN_SOURCE_SELECTOR);
  const drawTitle = resolveTitle(root, source);

  if (title && drawTitle) title.textContent = drawTitle;
  if (countdown && countdownSource) syncCountdown(countdown, countdownSource);

  cloneButton(wrapper, source);
  observeVisibility(wrapper, sources);
};
